import React, { useContext, useEffect, useState } from "react";

import { WeeklyContext } from "../../../../context/global/WeeklyContext";
import { DispatchContext } from "../../../../context/domain/planning/DispatchContext";

import { getDateString, formatDate } from "../../../../utils/datetime";
import {
  formatScopeNotesWithExtraSections,
  getUserDisplayName,
} from "../../../../utils/business";
import { getWeatherData } from "../../../../services/api/dispatch";
import { isDispatchVisibleAspect } from "../dispatchFilterModel";

import RSLogo from "../../../../components/icons/RSLogo";

/** Placeholder so capture still renders when weather lookup fails. */
const WEATHER_FALLBACK = {
  current: {
    condition: { text: "__________" },
    temp_f: "",
  },
};

/**
 * Printable daily job report used during review capture.
 *
 * @param {Object} props
 * @param {string} props.className Capture target classes, including job number.
 * @param {Object} props.job JobNumber payload for this report.
 * @returns {JSX.Element|null}
 */
const JobReport = ({ className, job }) => {
  const { scopes, today } = useContext(WeeklyContext);
  const { jobReportDate, dayDetails } = useContext(DispatchContext);
  const [weather, setWeather] = useState(null);
  const jobAddress = job.default_address?.address || "San Francisco, CA";
  const reportDate = getDateString(jobReportDate);
  const reportDayDetails = dayDetails.filter(
    (day) => day.date === reportDate
  )[0];
  const dayScopes = scopes?.filter(
    (scope) =>
      scope.date === reportDate &&
      scope.job.id === job.id &&
      isDispatchVisibleAspect(scope)
  );
  // We only ever generate the report for the most recent review
  const lastReviewedDispatch = reportDayDetails?.dispatch_stage
    .filter((dispatch) => dispatch.stage === "Reviewed")
    .sort((a, b) => a.id < b.id)[0];

  useEffect(() => {
    if (dayScopes && dayScopes.length > 0) {
      /**
       * Load weather for the report date/address; fall back so capture still renders.
       *
       * @param {void}
       * @returns {Promise<void>}
       */
      const fetchWeather = async () => {
        try {
          const weatherData = await getWeatherData(reportDate, jobAddress);
          setWeather(weatherData || WEATHER_FALLBACK);
        } catch {
          setWeather(WEATHER_FALLBACK);
        }
      };
      fetchWeather();
    }
  }, [jobAddress, reportDate, dayScopes]);

  if (!dayScopes || dayScopes.length === 0) {
    return null;
  }

  // There may be a case to move the following formatters to a global utils file if they
  // are subsequently used elsewhere in the application. Please do so if you re-use.
  /**
   * Join manager display names for the printable report header.
   *
   * @param {Array<Object>} managers Job manager users.
   * @returns {string}
   */
  const formatManagers = (managers) => {
    if (managers.length === 0) {
      return "None";
    }
    const string = managers.map((manager) => `${getUserDisplayName(manager)},`);
    return string.join(" ").slice(0, -1);
  };

  /**
   * Render one worker name, optionally with a list bullet.
   *
   * @param {Object} worker Worker user.
   * @param {boolean} showBullets Whether to prefix a bullet.
   * @returns {JSX.Element}
   */
  const formatWorker = (worker, showBullets) => {
    const bullets = showBullets ? "• " : "";
    return (
      <div>
        {bullets}
        {getUserDisplayName(worker)}
      </div>
    );
  };

  /**
   * Format one concrete line (mix, yards, time, supplier).
   *
   * @param {Object} concrete Concrete detail row.
   * @param {boolean} showBullets Whether to prefix a bullet.
   * @returns {string}
   */
  const formatConcrete = (concrete, showBullets) => {
    const bullets = showBullets ? "• " : "";
    const mix = concrete.mix;
    const string = `${bullets}${mix ? concrete.mix.design_number + ", " : ""}
    ${concrete.yards ? concrete.yards + " Yds, " : ""}${concrete.time && concrete.time.slice(0, -3) + ", "}${
      mix ? concrete.mix.supplier.name + ", " : ""
    }`;
    return string.slice(0, -2);
  };

  /**
   * Format one equipment line (asset name and rent/own).
   *
   * @param {Object} equipment Equipment detail row.
   * @param {boolean} showBullets Whether to prefix a bullet.
   * @returns {string}
   */
  const formatEquipment = (equipment, showBullets) => {
    const bullets = showBullets ? "• " : "";
    const string = `${bullets}${equipment.asset?.name ? equipment.asset.name + ", " : ""}
    ${equipment.rent ? "Rent, " : "Own, "}`;
    return string.slice(0, -2);
  };

  /**
   * Format one subcontractor line (name and hours).
   *
   * @param {Object} subcontractors Subcontractor detail row.
   * @param {boolean} showBullets Whether to prefix a bullet.
   * @returns {string}
   */
  const formatSubContractors = (subcontractors, showBullets) => {
    const bullets = showBullets ? "• " : "";
    const string = `${bullets}${subcontractors.contractor?.name && subcontractors.contractor.name + ", "}
    ${subcontractors.hours && subcontractors.hours + " Hrs, "}`;
    return string.slice(0, -2);
  };

  /**
   * Format one dumping line (contractor and quantity).
   *
   * @param {Object} dumping Dumping detail row.
   * @param {boolean} showBullets Whether to prefix a bullet.
   * @returns {string}
   */
  const formatDumping = (dumping, showBullets) => {
    const bullets = showBullets ? "• " : "";
    const string = `${bullets}${dumping.contractor?.name && dumping.contractor.name + ", "}
    ${dumping.quantity && dumping.quantity + " Qty, "}`;
    return string.slice(0, -2);
  };

  return (
    weather && (
      <div className={className}>
        <div className={`w-[816px]`}>
          <div className="bg-white p-4">
            <div className="flex justify-between mb-4 mx-4">
              <div className="grid grid-cols-10">
                <div className="col-span-2 mr-4">
                  <RSLogo classes="h-12" variant={"logo_trans"} />
                </div>
                <div className="col-span-8">
                  <p>R&S Construction Management Inc.</p>
                  <p>1555 Burke Ave STE I</p>
                  <p>San Francisco, CA 94124</p>
                  <p>(415) 872-9935</p>
                </div>
              </div>
              <h1 className="text-xl font-bold text-nowrap">
                Daily Job Report
              </h1>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4 border-2 border-gray-400 bg-gray-100 p-4">
              <div>
                <div>
                  <b>Job:</b> {job.number} {job.description}
                </div>
                <div>
                  <b>Work performed on</b> {formatDate(reportDate, "MM/DD/YY")}
                </div>
                <div>
                  <b>Form submitted on</b> {formatDate(today, "MM/DD/YY")},{" "}
                  {new Date().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </div>
              </div>
              <div>
                <div>
                  <b>Address:</b> {job.default_address?.address}
                </div>
                <p>
                  <b>Project Manager{job.managers.length > 1 ? "s" : ""}:</b>{" "}
                  {formatManagers(job.managers)}
                </p>
                <p>
                  <b>Weather:</b>{" "}
                  {weather?.current?.condition?.text || "__________"}
                </p>
                <p>
                  <b>Temperature:</b>{" "}
                  {weather?.current?.temp_f
                    ? `${weather.current.temp_f}°F`
                    : "__________"}
                </p>
              </div>
            </div>
            <div className="px-8">
              {dayScopes.map((scope) => (
                <div key={scope.id}>
                  <div className="mb-2">
                    <b>Work Scope: {scope.scope.title}</b>
                    {scope.custom_address && (
                      <div>
                        <b>Custom Address:</b> {scope.custom_address}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-10 mb-4 border-2 border-gray-400">
                    <div className="col-span-2 border-b-2 border-gray-400 bg-primary text-white pl-2">
                      <b>Workers</b>
                    </div>
                    <div className="col-span-8 border-b-2 border-gray-400 border-l-2 pl-2">
                      {scope.worker_assignment &&
                      scope.worker_assignment.length > 0 ? (
                        scope.worker_assignment.map((worker_assignment) => (
                          <div key={worker_assignment.id}>
                            {formatWorker(
                              worker_assignment,
                              scope.worker_assignment.length > 1
                            )}
                          </div>
                        ))
                      ) : (
                        <span>None</span>
                      )}
                    </div>
                    <div className="col-span-2 border-b-2 border-gray-400 bg-primary text-white pl-2">
                      <b>Trades</b>
                    </div>
                    <div className="col-span-8 border-b-2 border-gray-400 border-l-2 pl-2">
                      {scope.worker_assignment &&
                      scope.worker_assignment.length > 0 ? (
                        [
                          ...new Set(
                            scope.worker_assignment
                              .flatMap((worker) =>
                                worker.worker_type.map((type) => type.type)
                              )
                              .filter(
                                (type) =>
                                  type !== "Foreman" && type !== "Tool Truck"
                              )
                          ),
                        ].join(", ")
                      ) : (
                        <span>None</span>
                      )}
                    </div>
                    <div className="col-span-2 border-b-2 border-gray-400 bg-primary text-white pl-2">
                      <b>Concrete</b>
                    </div>
                    <div className="col-span-8 border-b-2 border-gray-400 border-l-2 pl-2">
                      {scope.concrete && scope.concrete.length > 0 ? (
                        scope.concrete.map((concrete) => (
                          <div key={concrete.id}>
                            {formatConcrete(
                              concrete,
                              scope.concrete.length > 1
                            )}
                          </div>
                        ))
                      ) : (
                        <span>None</span>
                      )}
                    </div>
                    <div className="col-span-2 border-b-2 border-gray-400 bg-primary text-white pl-2">
                      <b>Equipment</b>
                    </div>
                    <div className="col-span-8 border-b-2 border-gray-400 border-l-2 pl-2">
                      {scope.equipment && scope.equipment.length > 0 ? (
                        scope.equipment.map((equipment) => (
                          <div key={equipment.id}>
                            {formatEquipment(
                              equipment,
                              scope.equipment.length > 1
                            )}
                          </div>
                        ))
                      ) : (
                        <span>None</span>
                      )}
                    </div>
                    <div className="col-span-2 border-b-2 border-gray-400 bg-primary text-white pl-2">
                      <b>Subcontractor</b>
                    </div>
                    <div className="col-span-8 border-b-2 border-gray-400 border-l-2 pl-2">
                      {scope.subcontractor && scope.subcontractor.length > 0 ? (
                        scope.subcontractor.map((subcontractor) => (
                          <div key={subcontractor.id}>
                            {formatSubContractors(
                              subcontractor,
                              scope.subcontractor.length > 1
                            )}
                          </div>
                        ))
                      ) : (
                        <span>None</span>
                      )}
                    </div>
                    <div className="col-span-2 border-b-2 border-gray-400 bg-primary text-white pl-2">
                      <b>Dumping</b>
                    </div>
                    <div className="col-span-8 border-b-2 border-gray-400 border-l-2 pl-2">
                      {scope.dumping && scope.dumping.length > 0 ? (
                        scope.dumping.map((dumping) => (
                          <div key={dumping.id}>
                            {formatDumping(dumping, scope.dumping.length > 1)}
                          </div>
                        ))
                      ) : (
                        <span>None</span>
                      )}
                    </div>
                    <div className="col-span-2 border-b-2 pl-2 bg-primary text-white">
                      <b>Notes</b>
                    </div>
                    <div className="col-span-8 border-b-2 border-gray-400 border-l-2 pl-2">
                      <span className="whitespace-pre-wrap">
                        {formatScopeNotesWithExtraSections(
                          scope.notes,
                          scope.extra_data_sections
                        )}
                      </span>
                    </div>
                    <div className="col-span-2 border-b-2 pl-2 bg-primary text-white">
                      <b>Accidents</b>
                    </div>
                    <div className="col-span-8 border-b-2 border-gray-400 border-l-2 pl-2">
                      <span>
                        {lastReviewedDispatch?.accidents_reported
                          ? "None"
                          : "None"}
                      </span>
                    </div>
                    <div className="col-span-2 border-b-2 pl-2 bg-primary text-white">
                      <b>Change Orders</b>
                    </div>
                    <div className="col-span-8 border-b-2 border-gray-400 border-l-2 pl-2">
                      <span>
                        {lastReviewedDispatch?.change_orders_issued
                          ? "None"
                          : "None"}
                      </span>
                    </div>
                    <div className="col-span-2 border-b-2 pl-2 bg-primary text-white">
                      <b>T&M Work</b>
                    </div>
                    <div className="col-span-8 border-gray-400 border-l-2 pl-2">
                      <span>
                        {lastReviewedDispatch?.time_material_reported
                          ? "None"
                          : "None"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  );
};

export default JobReport;
